import { cn } from '@invent/wl-ui-kit-next';
import { EyeOff } from 'lucide-react';
import * as React from 'react';

import { PageHeader, useZone } from '@/design';
import { DEMO_ROLE_LABEL, type MockScreen as MockScreenId } from '../roles';
import { useMockGrant, type IMockGrant } from '../state/demo-state';
import { DemoStrip } from './marks';

/**
 * A whole mock screen, in the zone it is mounted in: the strip first — before the title, so it is
 * the first thing read — then the zone's own page header, then the screen.
 *
 * **"View as role" decides whether the screen opens**, by the role matrix: a role the matrix does
 * not grant the screen is told so in place, and shown nothing of it. The screen's controls read
 * `acts` from the same grant (`useScreenGrant`), so a role that opens a screen but may not act on
 * it sees it inert. The real guards have already let the page render; this narrows the mock only.
 */
const GrantContext = React.createContext<IMockGrant>({ opens: true, acts: true, role: null });

export function useScreenGrant(): IMockGrant {
  return React.useContext(GrantContext);
}

export function MockScreen({
  screen,
  title,
  lede,
  missing,
  crumb,
  actions,
  tabs,
  children
}: {
  screen: MockScreenId;
  title: string;
  lede?: React.ReactNode;
  /** What the product needs before this screen could be real — the strip's second line. */
  missing: React.ReactNode;
  crumb?: string;
  actions?: React.ReactNode;
  tabs?: React.ReactNode;
  children: React.ReactNode;
}) {
  const grant = useMockGrant(screen);
  const team = useZone() === 'team';

  return (
    <GrantContext.Provider value={grant}>
      {team ? (
        <DemoStrip missing={missing} />
      ) : (
        <div className="mx-auto w-full max-w-[1120px] px-6 pt-6 lg:px-10">
          <DemoStrip missing={missing} />
        </div>
      )}
      <PageHeader
        title={title}
        crumb={crumb}
        lede={lede}
        actions={grant.opens ? actions : undefined}
        tabs={tabs}
      />
      <div
        className={cn(
          team ? 'grid min-w-0 gap-6 px-6 py-6' : 'mx-auto grid w-full max-w-[1120px] gap-6 px-6 pt-6 pb-16 lg:px-10'
        )}
      >
        {grant.opens ? children : <NotForRole grant={grant} title={title} />}
      </div>
    </GrantContext.Provider>
  );
}

function NotForRole({ grant, title }: { grant: IMockGrant; title: string }) {
  return (
    <div
      role="status"
      className="flex max-w-2xl items-start gap-3 rounded-lg border border-dashed border-[var(--veritant-yellow-edge)] bg-white p-4"
    >
      <EyeOff className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <p>
        Viewing as <strong>{grant.role ? DEMO_ROLE_LABEL[grant.role] : 'signed in'}</strong>, who does not
        open {title} under the demo’s role matrix. Choose another role in the demo guide to see
        it — live screens are not changed by this.
      </p>
    </div>
  );
}

/** Says, where a control is inert for the role viewed as, why. */
export function ViewOnlyNote({ what }: { what: string }) {
  const { acts, role } = useScreenGrant();
  if (acts || !role) return null;
  return (
    <p className="text-[0.8125rem] text-muted-foreground">
      Viewing as {DEMO_ROLE_LABEL[role]}: {what} is read-only for this role.
    </p>
  );
}
